import {
  Box,
  Button,
  Collapse,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { tokens } from "../../../theme";
import type { EquipmentUnitFormData } from "../../../types/equipment";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import PhoneNumberField from "../../../components/ui/PhoneNumberField";

interface SupplierStepProps {
  formData: EquipmentUnitFormData;
  onChange: (field: keyof EquipmentUnitFormData, value: string) => void;
  onPrevious: () => void;
  onSubmit: () => void;
  onSave: () => void;
  isSubmitting: boolean;
}

export default function SupplierStep({
  formData,
  onChange,
  onPrevious,
  onSubmit,
  onSave,
  isSubmitting,
}: SupplierStepProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [showMore, setShowMore] = useState(false);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.primary[800],
      borderRadius: "16px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
    },
  };

  const labelSx = {
    fontWeight: 600,
    color: colors.primary[400],
    mb: 0.5,
    fontSize: "0.88rem",
  };

  const sectionLabelSx = {
    fontWeight: 600,
    color: colors.primary[400],
    mb: 0.5,
    fontSize: "0.88rem",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Typography sx={sectionLabelSx}>Financial</Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Purchase Cost
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. 2500"
            type="number"
            size="small"
            value={formData.purchase_cost}
            onChange={(e) => onChange("purchase_cost", e.target.value)}
            sx={inputSx}
          />
        </Box>
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Current Value
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. 2000"
            type="number"
            size="small"
            value={formData.current_value}
            onChange={(e) => onChange("current_value", e.target.value)}
            sx={inputSx}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Purchase Date
          </Typography>
          <TextField
            fullWidth
            type="date"
            size="small"
            value={formData.purchase_date}
            onChange={(e) => onChange("purchase_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />
        </Box>
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Warranty Expiry
          </Typography>
          <TextField
            fullWidth
            type="date"
            size="small"
            value={formData.warranty_expiry_date}
            onChange={(e) => onChange("warranty_expiry_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />
        </Box>
      </Box>

      <Typography sx={{ ...sectionLabelSx, mt: 0.5 }}>Supplier</Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Supplier Name
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. Megatech"
            size="small"
            value={formData.supplier_name}
            onChange={(e) => onChange("supplier_name", e.target.value)}
            sx={inputSx}
          />
        </Box>
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Contact Name
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. John Doe"
            size="small"
            value={formData.contact_name}
            onChange={(e) => onChange("contact_name", e.target.value)}
            sx={inputSx}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Phone
          </Typography>
          <PhoneNumberField
            value={formData.supplier_phone}
            onChange={(val) => onChange("supplier_phone", val)}
            size="small"
            sx={inputSx}
          />
        </Box>
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Email
          </Typography>
          <TextField
            fullWidth
            placeholder="supplier@example.com"
            size="small"
            type="email"
            value={formData.supplier_email}
            onChange={(e) => onChange("supplier_email", e.target.value)}
            sx={inputSx}
          />
        </Box>
      </Box>

      <Box>
        <Box
          onClick={() => setShowMore((p) => !p)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            userSelect: "none",
            color: colors.primary[400],
          }}
        >
          {showMore ? (
            <ExpandMore sx={{ fontSize: 18 }} />
          ) : (
            <ChevronRight sx={{ fontSize: 18 }} />
          )}
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 600,
              color: colors.primary[400],
            }}
          >
            More supplier info (website)
          </Typography>
        </Box>

        <Collapse in={showMore}>
          <Box sx={{ mt: 2, width: { xs: "100%", sm: "48%" } }}>
            <Typography variant="body2" sx={labelSx}>
              Website
            </Typography>
            <TextField
              fullWidth
              placeholder="https://"
              size="small"
              type="url"
              value={formData.supplier_website}
              onChange={(e) => onChange("supplier_website", e.target.value)}
              sx={inputSx}
            />
          </Box>
        </Collapse>
      </Box>

      {/* Nav */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 3 }}>
          <Typography
            onClick={onPrevious}
            sx={{
              cursor: "pointer",
              fontWeight: 600,
              textDecoration: "underline",
              color: colors.primary[400],
              fontSize: "0.9rem",
            }}
          >
            Previous
          </Typography>
          <Typography
            onClick={onSave}
            sx={{
              cursor: "pointer",
              fontWeight: 600,
              textDecoration: "underline",
              color: colors.primary[400],
              fontSize: "0.9rem",
            }}
          >
            Save Gear
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={onSubmit}
          disabled={isSubmitting}
          sx={{
            backgroundColor: colors.primary[400],
            px: 5,
            py: 1.2,
            borderRadius: "24px",
            "&.Mui-disabled": {
              backgroundColor: colors.primary[800],
              color: colors.primary[900],
            },
          }}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Box>
  );
}
